/**
 * `/accounts` — the account list, and the rep-firm list beside it.
 *
 * Rep firms live here rather than under Admin because a rep looking for "who
 * covers this distributor" is not doing administration. Editing one is still
 * Admin-only (§2 reference data), and the affordance says so instead of
 * offering a control the API will refuse.
 *
 * Creating an account is offered here, from the header and from the empty
 * state, through the **same `CreateAccountDialog`** the account picker opens.
 * One dialog, because two forms would be two answers to what a valid account
 * is — and the dialog is where §6.8's warn-never-block duplicate check lives.
 * Ungated, like `New opportunity`: `TIERS` has no read-only tier, so a
 * `canWrite` gate would hide nothing from anyone, and the server enforces (§2
 * rule 1).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { CHANNEL_TYPES, REGIONS, SEGMENTS } from '@menlo/shared';
import { useApiQueryAllPages, type Row } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { useToast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TabList, TabPanel, useTab, type TabDefinition } from '@/components/ui/tabs';
import { CreateAccountDialog } from '@/features/pickers/AccountPicker';
import { humanize } from '@/lib/format';

const TABS: TabDefinition[] = [
  { id: 'accounts', label: 'Accounts' },
  { id: 'rep-firms', label: 'Rep firms & distributors' },
];

export function AccountsList(): React.ReactElement {
  const reference = useReferenceData();
  const navigate = useNavigate();
  const { push } = useToast();
  const [tab, setTab] = useTab(TABS);
  /** `null` is closed; a string is the name the dialog opens with. */
  const [creating, setCreating] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [segment, setSegment] = useState('');
  const [region, setRegion] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [channel, setChannel] = useState('');

  /**
   * EVERY account, page by page, not one page of 200: the text search and the
   * channel filter run here (a term may not travel in a query string, §9), and
   * the nesting below needs each site and its parent in the same set. A single
   * page silently dropped every account past the 200th by name once dev had
   * more than that (session 6). The list is a few hundred rows; when it is
   * thousands this wants server-side search and real paging instead.
   */
  const query = new URLSearchParams({ sort: 'name' });
  if (segment !== '') query.set('segment', segment);
  if (region !== '') query.set('region', region);
  if (ownerId !== '') query.set('ownerId', ownerId);

  const accounts = useApiQueryAllPages<Row>(
    ['accounts', query.toString()],
    `/api/accounts?${query.toString()}`,
  );

  /**
   * Channel and free text are filtered here rather than on the server: the
   * accounts endpoint takes region, segment, owner and status, and a term must
   * not travel in a query string (§9). The list is small enough that the
   * honest answer is to say so rather than add a search endpoint nothing else
   * needs.
   */
  const items = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return (accounts.data?.items ?? []).filter(
      (account) =>
        (channel === '' || account['channel_type'] === channel) &&
        (needle === '' || String(account['name']).toLowerCase().includes(needle)),
    );
  }, [accounts.data, channel, term]);

  const byParent = useMemo(() => {
    const roots = items.filter(
      (account) =>
        account['parent_account_id'] === null || account['parent_account_id'] === undefined,
    );
    const children = new Map<string, Row[]>();
    for (const account of items) {
      const parent = account['parent_account_id'];
      if (typeof parent !== 'string') continue;
      children.set(parent, [...(children.get(parent) ?? []), account]);
    }
    // An account whose parent is filtered out still has to appear somewhere.
    const orphans = items.filter(
      (account) =>
        typeof account['parent_account_id'] === 'string' &&
        !items.some((candidate) => candidate['id'] === account['parent_account_id']),
    );
    return { roots: [...roots, ...orphans], children };
  }, [items]);

  /**
   * "Nothing here yet" and "nothing matched" are different situations and need
   * different words: the first is a new environment, the second is a filter
   * the user can widen. Every filter counts, the two client-side ones
   * included, so a channel filter that hides everything does not get reported
   * as an empty database.
   */
  const unfiltered =
    term.trim() === '' && segment === '' && region === '' && ownerId === '' && channel === '';

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Accounts</h1>
        {/* Only on the accounts tab: beside the rep-firm list this would read
            as "new rep firm", which is Admin-only reference data. */}
        {tab === 'accounts' && (
          <Button size="sm" onClick={() => setCreating('')}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New account
          </Button>
        )}
      </header>
      <TabList tabs={TABS} active={tab} onSelect={setTab} />

      <TabPanel id="accounts" active={tab}>
        <div className="mb-3 flex flex-wrap gap-2">
          <Input
            aria-label="Search accounts"
            placeholder="Search…"
            className="w-56"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          <Select
            aria-label="Segment"
            className="w-auto"
            value={segment}
            onChange={(event) => setSegment(event.target.value)}
          >
            <option value="">All segments</option>
            {SEGMENTS.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Region"
            className="w-auto"
            value={region}
            onChange={(event) => setRegion(event.target.value)}
          >
            <option value="">All regions</option>
            {REGIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Owner"
            className="w-auto"
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
          >
            <option value="">All owners</option>
            {reference.users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Channel"
            className="w-auto"
            value={channel}
            onChange={(event) => setChannel(event.target.value)}
          >
            <option value="">All channels</option>
            {CHANNEL_TYPES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </div>

        <ul className="divide-y rounded-md border">
          {byParent.roots.map((account) => (
            <li key={String(account['id'])} className="px-3 py-2">
              <AccountRow account={account} />
              {(byParent.children.get(String(account['id'])) ?? []).length > 0 && (
                <ul className="mt-1 space-y-1 border-l pl-4">
                  {(byParent.children.get(String(account['id'])) ?? []).map((child) => (
                    <li key={String(child['id'])}>
                      <AccountRow account={child} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {items.length === 0 && (
            <li className="space-y-2 px-3 py-6 text-center text-xs text-muted-foreground">
              {accounts.isLoading ? (
                <p>Loading…</p>
              ) : unfiltered ? (
                <>
                  <p>
                    No accounts yet. An account is where a contact or an opportunity hangs, so it is
                    the first thing to create.
                  </p>
                  <Button size="sm" onClick={() => setCreating('')}>
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    Create the first account
                  </Button>
                </>
              ) : (
                <>
                  <p>No accounts match this search or these filters.</p>
                  {/* The same dialog the header opens, seeded with what was
                      typed — not a second affordance, just a second door. */}
                  <Button variant="outline" size="sm" onClick={() => setCreating(term.trim())}>
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    {term.trim() === '' ? 'New account' : `Create “${term.trim()}”`}
                  </Button>
                </>
              )}
            </li>
          )}
        </ul>
      </TabPanel>

      <TabPanel id="rep-firms" active={tab}>
        <ul className="divide-y rounded-md border">
          {reference.rep_firms.map((firm) => (
            <li key={firm.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span>
                <Link to={`/rep-firms/${firm.id}`} className="font-medium hover:underline">
                  {firm.name}
                </Link>
                <span className="ml-2 text-xs text-muted-foreground">
                  {humanize(firm.firm_type)} · {firm.region}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">
                net factor {firm.net_factor} ({firm.markup_pct}% markup)
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">
          The net factor converts a distributor&rsquo;s resale price to what Menlo books. Only an
          administrator can change it, and changing it rescales every net figure from that moment
          on.
        </p>
      </TabPanel>

      {/*
        Landing on the new account is the point: the reason to create one is to
        put a contact or an opportunity on it, and both of those live on the
        detail page. The list still refreshes behind us — the dialog
        invalidates `['accounts']` — so coming back shows the new row without a
        reload. The toast is there because the detail page of a brand-new
        account looks like any other page.
      */}
      {creating !== null && (
        <CreateAccountDialog
          initialName={creating}
          onClose={() => setCreating(null)}
          onCreated={(account) => {
            setCreating(null);
            push({ tone: 'success', title: `${account.name} created` });
            navigate(`/accounts/${account.id}`);
          }}
        />
      )}
    </div>
  );
}

function AccountRow({ account }: { account: Row }): React.ReactElement {
  return (
    <span className="flex items-center justify-between gap-2 text-sm">
      <span className="min-w-0">
        <Link to={`/accounts/${String(account['id'])}`} className="font-medium hover:underline">
          {String(account['name'])}
        </Link>
        <span className="ml-2 text-xs text-muted-foreground">
          {humanize(String(account['segment']))} · {String(account['region'])} ·{' '}
          {String(account['owner_name'] ?? 'unowned')}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1">
        {account['export_sensitive'] === true && <Badge variant="risk">Export-sensitive</Badge>}
        <Badge variant="outline">{humanize(String(account['channel_type']))}</Badge>
      </span>
    </span>
  );
}
